// Pure structural validation for a workflow definition draft — no API calls. Runs entirely over
// data already loaded/assembled client-side (stages/statuses/transitions/hooks), computed fresh
// on every render of the wizard's Validate & Publish step. Mirrors (and extends) the entry/
// terminal-status check that already exists server-side for tenant customization
// (`_assert_entry_and_terminal_intact` in api-service's customization_service.py) — but that
// check never runs against platform WorkflowDefinition create/update, so it's reproduced here
// alongside reachability and duplicate-transition checks that have no server-side equivalent at
// all yet.
import type {
  AutomationHook,
  EntryPointDefinition,
  StatusDefinition,
  TransitionDefinition,
} from '@/redux/internal-services/workflowApi';

export interface ValidationCheck {
  id: string;
  label: string;
  /** 'pass' | 'warn' — this module never hard-blocks; MVP treats every finding as informational,
   *  same as the mockup's "0 blocking issues, 1 warning. Ready to publish." */
  severity: 'pass' | 'warn';
  detail?: string;
}

export interface ValidationInput {
  statuses: StatusDefinition[];
  transitions: TransitionDefinition[];
  entryPoints: EntryPointDefinition[];
  automationHooks: AutomationHook[];
}

const KNOWN_ACTION_TYPES = new Set([
  'create_task', 'send_notification', 'request_approval', 'update_field', 'trigger_module_action', 'webhook',
  // Also accept the older/alternate labels already used by WorkflowEditorPage's hook form.
  'task_creation', 'approval_creation', 'notification', 'follow_up', 'module_action',
]);

export function validateWorkflowDraft({ statuses, transitions, entryPoints, automationHooks }: ValidationInput): ValidationCheck[] {
  const checks: ValidationCheck[] = [];

  // 1. Exactly one entry point per engagement type covered.
  const entryStatusCodes = new Set(
    entryPoints.length > 0
      ? entryPoints.map((e) => e.status_code)
      : statuses.filter((s) => s.lifecycle_role === 'entry').map((s) => s.status_code),
  );
  checks.push(
    entryStatusCodes.size >= 1
      ? { id: 'entry', label: 'At least one entry point defined', severity: 'pass' }
      : { id: 'entry', label: 'No entry point defined', severity: 'warn', detail: 'Mark one status as the entry point (or add an Entry Point record) before publishing.' },
  );

  // 2. At least one terminal status.
  const terminalStatuses = statuses.filter((s) => s.lifecycle_role === 'terminal');
  checks.push(
    terminalStatuses.length >= 1
      ? { id: 'terminal', label: 'At least one terminal status', severity: 'pass' }
      : { id: 'terminal', label: 'No terminal status defined', severity: 'warn', detail: 'At least one status should be marked terminal so engagements can complete.' },
  );

  // 3. Every status reachable from an entry point (BFS over transitions).
  const adjacency = new Map<string, string[]>();
  for (const t of transitions) {
    adjacency.set(t.source_status_code, [...(adjacency.get(t.source_status_code) ?? []), t.target_status_code]);
  }
  const reached = new Set<string>();
  const queue = [...entryStatusCodes];
  while (queue.length > 0) {
    const code = queue.shift()!;
    if (reached.has(code)) continue;
    reached.add(code);
    for (const next of adjacency.get(code) ?? []) {
      if (!reached.has(next)) queue.push(next);
    }
  }
  const unreachable = statuses.filter((s) => !reached.has(s.status_code) && !entryStatusCodes.has(s.status_code));
  checks.push(
    unreachable.length === 0
      ? { id: 'reachability', label: 'Every status is reachable', severity: 'pass' }
      : {
          id: 'reachability',
          label: `${unreachable.length} status${unreachable.length === 1 ? '' : 'es'} not yet reachable`,
          severity: 'warn',
          detail: `${unreachable.map((s) => s.name).join(', ')} — add a transition into ${unreachable.length === 1 ? 'it' : 'them'}, or ignore if reached only by direct assignment.`,
        },
  );

  // 4. Duplicate source→target transitions (same pair defined more than once).
  const pairCounts = new Map<string, number>();
  for (const t of transitions) {
    const key = `${t.source_status_code}→${t.target_status_code}`;
    pairCounts.set(key, (pairCounts.get(key) ?? 0) + 1);
  }
  const duplicates = [...pairCounts.entries()].filter(([, count]) => count > 1);
  checks.push(
    duplicates.length === 0
      ? { id: 'duplicates', label: 'No duplicate transitions', severity: 'pass' }
      : { id: 'duplicates', label: `${duplicates.length} duplicate transition pair${duplicates.length === 1 ? '' : 's'}`, severity: 'warn', detail: duplicates.map(([pair]) => pair).join(', ') },
  );

  // 5. All transitions have at least one authorized role is checked at the authority-rules
  // level elsewhere in the builder — out of scope for the wizard's lightweight pass, so this
  // check is limited to what the wizard actually collects: hooks reference a registered action type.
  const unknownHooks = automationHooks.filter((h) => !KNOWN_ACTION_TYPES.has(h.action_type));
  checks.push(
    unknownHooks.length === 0
      ? { id: 'hooks', label: 'Automation hooks reference registered actions', severity: 'pass' }
      : { id: 'hooks', label: `${unknownHooks.length} hook(s) reference an unregistered action type`, severity: 'warn', detail: unknownHooks.map((h) => h.hook_code).join(', ') },
  );

  return checks;
}
